import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { 
  FiArrowRight, 
  FiShield, 
  FiZap, 
  FiUsers, 
  FiTrendingUp, 
  FiStar,
  FiCheckCircle,
  FiClock,
  FiDollarSign,
  FiGlobe,
  FiAward,
  FiSmartphone,
  FiBarChart2,
  FiBriefcase
} from 'react-icons/fi';
import { 
  FaFacebook, 
  FaInstagram, 
  FaTwitter, 
  FaWhatsapp, 
  FaTelegram, 
  FaTiktok,
  FaLinkedin,
  FaYoutube,
  FaPhoneAlt
} from 'react-icons/fa';
import { HiOutlineUserGroup, HiOutlineChartBar } from 'react-icons/hi';
import { BsGraphUp, BsPhone, BsRocket, BsShieldCheck } from 'react-icons/bs';
import { Helmet } from 'react-helmet-async';
import CountUp from 'react-countup';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Pagination, EffectCoverflow } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/effect-coverflow';
import { SupportButton } from '../components/ui/SupportButton';

export const HomePage = () => {
  const controls = useAnimation();
  const [ref, inView] = useInView({ threshold: 0.3, triggerOnce: true });

  useEffect(() => {
    if (inView) {
      controls.start('visible');
    }
  }, [controls, inView]);

  const fadeInUp = {
    hidden: { opacity: 0, y: 60 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.8, ease: "easeOut" }
    }
  };

  const fadeInLeft = {
    hidden: { opacity: 0, x: -60 },
    visible: { 
      opacity: 1, 
      x: 0,
      transition: { duration: 0.8, ease: "easeOut" }
    }
  };

  const fadeInRight = {
    hidden: { opacity: 0, x: 60 },
    visible: { 
      opacity: 1, 
      x: 0,
      transition: { duration: 0.8, ease: "easeOut" }
    }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  };

  const pageTitle = 'Nitro Logs | Verified Social Media Accounts, Growth Services & Virtual Numbers';
  const pageDescription = 'Buy verified social media accounts for Facebook, Instagram, Twitter and TikTok. Grow with organic boosting services, secure SMS-enabled virtual numbers, instant delivery and trusted support.';
  const siteUrl = typeof window !== 'undefined' ? window.location.origin : 'https://nitrologs.com';
  const pageUrl = typeof window !== 'undefined' ? window.location.href : `${siteUrl}/`;
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    url: pageUrl,
    name: pageTitle,
    description: pageDescription,
    publisher: {
      '@type': 'Organization',
      name: 'Nitro Logs',
      url: siteUrl,
      logo: `${siteUrl}/nitrologs.png`
    }
  };

  const stats = [
    { value: 15000, label: 'Happy Customers', suffix: '+', icon: FiUsers },
    { value: 25000, label: 'Accounts Sold', suffix: '+', icon: FiTrendingUp },
    { value: 50000, label: 'Numbers Delivered', suffix: '+', icon: FiSmartphone },
    { value: 24, label: 'Support', suffix: '/7', icon: FiClock },
  ];

  const services = [
    {
      icon: FaFacebook,
      title: 'Social Media Accounts',
      description: 'Buy verified accounts with real followers. Facebook, Instagram, Twitter, TikTok and more.',
      features: ['Real followers', 'Email access', 'Instant delivery', 'Money-back guarantee'],
      color: 'from-blue-500 to-blue-600',
      gradient: 'bg-gradient-to-br from-blue-500/10 to-blue-600/5',
      border: 'border-blue-500/20',
      link: '/accounts'
    },
    {
      icon: BsGraphUp,
      title: 'Social Media Boosting',
      description: 'Grow your presence organically with our premium boosting services.',
      features: ['Real engagement', 'Targeted audience', 'Safe & secure', 'Gradual growth'],
      color: 'from-cyan-500 to-blue-500',
      gradient: 'bg-gradient-to-br from-cyan-500/10 to-blue-500/5',
      border: 'border-cyan-500/20',
      link: '/social-boost'
    },
    {
      icon: FaPhoneAlt,
      title: 'Virtual Numbers',
      description: 'Get virtual phone numbers for SMS verification and account creation.',
      features: ['50+ countries', 'SMS capable', 'Temporary & permanent', 'Affordable rates'],
      color: 'from-blue-600 to-indigo-600',
      gradient: 'bg-gradient-to-br from-blue-600/10 to-indigo-600/5',
      border: 'border-blue-600/20',
      link: '/virtual-numbers'
    },
  ];

  const categories = [
    { icon: FaFacebook, name: 'Facebook', count: '5,200+', color: 'from-blue-600 to-blue-400' },
    { icon: FaInstagram, name: 'Instagram', count: '4,800+', color: 'from-blue-500 to-cyan-400' },
    { icon: FaTwitter, name: 'Twitter', count: '3,500+', color: 'from-sky-400 to-blue-500' },
    { icon: FaWhatsapp, name: 'WhatsApp', count: '2,900+', color: 'from-blue-500 to-blue-600' },
    { icon: FaTelegram, name: 'Telegram', count: '2,100+', color: 'from-blue-400 to-cyan-400' },
    { icon: FaTiktok, name: 'TikTok', count: '3,800+', color: 'from-blue-700 to-blue-500' },
    { icon: FaLinkedin, name: 'LinkedIn', count: '1,800+', color: 'from-blue-700 to-blue-500' },
    { icon: FaYoutube, name: 'YouTube', count: '2,400+', color: 'from-blue-600 to-blue-400' },
  ];

  const testimonials = [
    {
      name: 'Sarah Johnson',
      role: 'Digital Marketing Manager',
      content: 'The quality of accounts is exceptional. I\'ve purchased multiple Instagram accounts and all have performed beyond expectations. The verification process is thorough.',
      rating: 5,
      image: 'https://randomuser.me/api/portraits/women/1.jpg',
      company: 'GrowthHackers Inc',
    },
    {
      name: 'Michael Chen',
      role: 'E-commerce Entrepreneur',
      content: 'This platform saved me months of organic growth. The Facebook accounts I bought came with engaged audiences that converted immediately. Best investment this year.',
      rating: 5,
      image: 'https://randomuser.me/api/portraits/men/2.jpg',
      company: 'Chen Enterprises',
    },
    {
      name: 'Emily Rodriguez',
      role: 'Social Media Influencer',
      content: 'As an influencer, having multiple accounts across platforms is crucial. Nitro Logs made it easy to expand my presence. The accounts are legitimate.',
      rating: 5,
      image: 'https://randomuser.me/api/portraits/women/3.jpg',
      company: 'StyleByEmily',
    },
    {
      name: 'David Okafor',
      role: 'Agency Owner',
      content: 'We manage multiple client accounts and Nitro Logs has been our go-to source. Reliable, secure, and excellent customer service. Highly recommended.',
      rating: 5,
      image: 'https://randomuser.me/api/portraits/men/4.jpg',
      company: 'Digital Rise Agency',
    },
  ];

  const howItWorks = [
    {
      step: 1,
      title: 'Choose Service',
      description: 'Select from accounts, boosting, or virtual numbers based on your needs.',
      icon: '🎯',
    },
    {
      step: 2,
      title: 'Make Payment',
      description: 'Add funds securely via Korapay. Multiple payment options available.',
      icon: '💰',
    },
    {
      step: 3,
      title: 'Instant Delivery',
      description: 'Receive your account details or boosting service immediately.',
      icon: '⚡',
    },
    {
      step: 4,
      title: 'Start Growing',
      description: 'Take control and start building your online presence today.',
      icon: '🚀',
    },
  ];

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta name="keywords" content="verified social media accounts, Instagram accounts, TikTok accounts, virtual phone numbers, SMS verification, social media boosting, secure payments" />
        <meta name="robots" content="index, follow" />
        <meta name="theme-color" content="#0B1120" />

        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:image" content={`${siteUrl}/nitrologs.png`} />
        <meta property="og:image:alt" content="Nitro Logs verified social media services" />
        <meta property="og:site_name" content="Nitro Logs" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={`${siteUrl}/nitrologs.png`} />

        <link rel="canonical" href={pageUrl} />
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      </Helmet>

      {/* Dark Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-linear-to-b from-[#0B1120] to-[#1a2639]">
        {/* Animated Background */}
        <div className="absolute inset-0">
          {/* Dark gradient base */}
          <div className="absolute inset-0 bg-[#0B1120]" />
          
          {/* Animated Blue Orbs */}
          <motion.div
            animate={{ 
              x: [0, 200, 0],
              y: [0, -200, 0],
              scale: [1, 1.5, 1],
            }}
            transition={{ duration: 20, repeat: Infinity }}
            className="absolute top-20 left-20 w-[500px] h-[500px] bg-blue-500/10 rounded-full blur-[120px]"
          />
          <motion.div
            animate={{ 
              x: [0, -200, 0],
              y: [0, 200, 0],
              scale: [1, 1.3, 1],
            }}
            transition={{ duration: 25, repeat: Infinity }}
            className="absolute bottom-20 right-20 w-[600px] h-[600px] bg-cyan-500/10 rounded-full blur-[120px]"
          />
          
          {/* Grid Pattern */}
          <div className="absolute inset-0 opacity-20">
            <svg className="w-full h-full">
              <defs>
                <pattern id="grid" x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse">
                  <path d="M 40 0 L 0 0 0 40" fill="none" stroke="rgba(59,130,246,0.2)" strokeWidth="1" />
                </pattern>
              </defs>
              <rect x="0" y="0" width="100%" height="100%" fill="url(#grid)" />
            </svg>
          </div>

          {/* Animated Lines */}
          <div className="absolute inset-0 opacity-10">
            <svg className="absolute top-0 left-0 w-full h-full">
              <defs>
                <pattern id="lines" x="0" y="0" width="60" height="60" patternUnits="userSpaceOnUse">
                  <path d="M 60 0 L 0 60" stroke="rgba(59,130,246,0.3)" strokeWidth="1" />
                </pattern>
              </defs>
              <rect x="0" y="0" width="100%" height="100%" fill="url(#lines)" />
            </svg>
          </div>
        </div>

        {/* Hero Content */}
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 pt-10">
          <div className="grid lg:grid-cols-2 lg:gap-12 items-center">
            {/* Left Column */}
            <motion.div
              initial="hidden"
              animate="visible"
              variants={staggerContainer}
              className="pt-10 text-center lg:text-left"
            >

              <motion.h1 
                variants={fadeInUp}
                className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold mt-6 leading-tight"
              >
                <span className="text-white">Verified Social Media Accounts,</span>
                <br />
                <span className="text-transparent bg-clip-text bg-linear-to-r from-blue-400 via-cyan-400 to-blue-500">
                  Growth Services & Virtual Numbers
                </span>
              </motion.h1>

              <motion.p 
                variants={fadeInUp}
                className="text-lg sm:text-xl text-gray-300 mt-6 leading-relaxed max-w-lg mx-auto lg:mx-0"
              >
                Purchase verified Facebook, Instagram, Twitter and TikTok accounts with real followers. Grow securely using organic boosting and SMS-ready virtual phone numbers.
                <span className="block mt-2 text-blue-400 font-semibold">Fast delivery, trusted support, and secure payments.</span>
              </motion.p>

              <motion.div 
                variants={fadeInUp}
                className="flex flex-wrap gap-4 mt-8 justify-center lg:justify-start"
              >
                <Link to="/accounts">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="bg-linear-to-r from-blue-600 to-cyan-600 text-white font-semibold py-3 px-8 rounded-xl hover:shadow-lg hover:shadow-blue-500/25 transition-all duration-300 group"
                  >
                    <span className="flex items-center">
                      Get Started
                      <FiArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
                    </span>
                  </motion.button>
                </Link>
                <a
                  href="#how-it-works"
                  onClick={(e) => {
                    e.preventDefault();
                    document.querySelector('#how-it-works').scrollIntoView({ behavior: 'smooth' });
                  }}
                >
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="bg-white/5 backdrop-blur-sm border border-blue-500/20 text-white font-semibold py-3 px-8 rounded-xl hover:bg-white/10 transition-all duration-300"
                  >
                    How It Works
                  </motion.button>
                </a>
              </motion.div>

              {/* Trust Badges */}
              {/* <motion.div 
                variants={fadeInUp}
                className="flex items-center justify-center lg:justify-start space-x-6 mt-10"
              >
                <div className="flex -space-x-3">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <motion.img
                      key={i}
                      whileHover={{ scale: 1.1, zIndex: 10 }}
                      src={`https://randomuser.me/api/portraits/${i % 2 ? 'women' : 'men'}/${i}.jpg`}
                      alt="Customer"
                      className="w-10 h-10 rounded-full border-2 border-blue-500/30"
                    />
                  ))}
                </div>
                <div className="flex flex-col">
                  <div className="flex items-center space-x-1">
                    {[...Array(5)].map((_, i) => (
                      <FiStar key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <span className="text-sm text-gray-400 font-medium mt-1">
                    <span className="font-bold text-white">4.9/5</span> from 2.5k+ reviews
                  </span>
                </div>
              </motion.div> */}
            </motion.div>

            {/* Right Column - 3D Card Showcase */}
            <motion.div
              variants={fadeInRight}
              className="relative block"
            >
              <div className="relative h-[600px] perspective-1000">
                {/* Main Floating Card */}
                <motion.div
                  animate={{ 
                    rotateY: [0, 10, 0, -10, 0],
                    y: [0, -15, 0, -15, 0]
                  }}
                  transition={{ duration: 12, repeat: Infinity }}
                  className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-86 lg:w-96"
                  style={{ transformStyle: 'preserve-3d' }}
                >
                  <div className="bg-linear-to-br from-gray-900 to-gray-800 p-8 rounded-3xl border border-blue-500/20 shadow-2xl backdrop-blur-xl">
                    <div className="flex items-center space-x-4 mb-6">
                      <div className="w-16 h-16 bg-linear-to-r from-blue-500 to-cyan-500 rounded-2xl flex items-center justify-center">
                        <FaFacebook className="w-8 h-8 text-white" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-white">Facebook Business</h3>
                        <p className="text-gray-400">Verified • 5 years old</p>
                      </div>
                    </div>
                    
                    <div className="space-y-4 mb-6">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400">Followers</span>
                        <span className="text-2xl font-bold text-white">25.4K</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400">Engagement Rate</span>
                        <span className="text-2xl font-bold text-blue-400">8.2%</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400">Posts</span>
                        <span className="text-2xl font-bold text-white">1.2K</span>
                      </div>
                    </div>
                    
                    <div className="flex justify-between items-center pt-4 border-t border-gray-700">
                      <span className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">
                        ₦45,000
                      </span>
                      <span className="px-4 py-2 bg-blue-500/10 text-blue-400 rounded-full text-sm font-semibold border border-blue-500/20">
                        Available Now
                      </span>
                    </div>
                  </div>
                </motion.div>

                {/* Floating Badges */}
                <motion.div
                  animate={{ y: [0, -10, 0], rotate: [0, 5, 0] }}
                  transition={{ duration: 5, repeat: Infinity }}
                  className="absolute top-15 lg:top-20 right-0 w-26 h-24 lg:w-32 lg:h-32 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-2xl shadow-2xl flex items-center justify-center"
                >
                  <div className="text-center text-white">
                    <div className="text-3xl font-bold">98%</div>
                    <div className="text-xs">Success Rate</div>
                  </div>
                </motion.div>

                <motion.div
                  animate={{ y: [0, 10, 0], rotate: [0, -5, 0] }}
                  transition={{ duration: 6, repeat: Infinity }}
                  className="absolute bottom-15 lg:bottom-40 left-0 w-22 lg:w-28 h-24 lg:h-28 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-2xl shadow-2xl flex items-center justify-center"
                >
                  <div className="text-center text-white">
                    <div className="text-2xl font-bold">24/7</div>
                    <div className="text-xs">Support</div>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section - Dark */}
      <section className="py-20 bg-[#0B1120]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="text-center"
                >
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-500/10 rounded-2xl mb-4 border border-blue-500/20">
                    <Icon className="w-8 h-8 text-blue-400" />
                  </div>
                  <div className="text-3xl lg:text-4xl font-bold text-white">
                    <CountUp end={stat.value} duration={2.5} suffix={stat.suffix} />
                  </div>
                  <p className="text-gray-400 mt-2">{stat.label}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-[#0f172a]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <span className="text-blue-400 font-semibold text-sm uppercase tracking-wider">Our Services</span>
            <h2 className="text-4xl lg:text-5xl font-display font-bold mt-4 text-white">
              Everything You Need to <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">Succeed</span>
            </h2>
            <p className="text-xl text-gray-400 mt-4">
              Complete suite of social media solutions tailored for your growth
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -5 }}
                  className={`${service.gradient} p-8 rounded-2xl border ${service.border} backdrop-blur-sm`}
                >
                  <div className={`w-14 h-14 bg-gradient-to-r ${service.color} rounded-xl flex items-center justify-center mb-6`}>
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-3">{service.title}</h3>
                  <p className="text-gray-400 mb-4">{service.description}</p>
                  <ul className="space-y-2 mb-6">
                    {service.features.map((feature, i) => (
                      <li key={i} className="flex items-center text-sm text-gray-300">
                        <FiCheckCircle className="w-4 h-4 text-blue-400 mr-2" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <Link to={service.link}>
                    <button className="w-full py-3 bg-blue-500/10 border border-blue-500/20 text-blue-400 rounded-xl hover:bg-blue-500/20 transition-all duration-300 flex items-center justify-center group">
                      Learn More
                      <FiArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
                    </button>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-20 bg-[#0B1120]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <span className="text-blue-400 font-semibold text-sm uppercase tracking-wider">Platforms</span>
            <h2 className="text-4xl lg:text-5xl font-display font-bold mt-4 text-white">
              All Major <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">Platforms</span>
            </h2>
            <p className="text-xl text-gray-400 mt-4">
              Choose from thousands of accounts across all major social media platforms
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
            {categories.map((category, index) => {
              const Icon = category.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -5 }}
                >
                  <Link to={`/accounts?type=${category.name}`}>
                    <div className="bg-gray-800/50 p-6 rounded-xl text-center border border-gray-700 hover:border-blue-500/30 transition-all duration-300">
                      <div className={`w-12 h-12 mx-auto mb-3 rounded-lg bg-gradient-to-r ${category.color} p-2 flex items-center justify-center`}>
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                      <h3 className="font-semibold text-sm text-gray-300">{category.name}</h3>
                      <p className="text-xs text-gray-500 mt-1">{category.count}</p>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-20 bg-[#0f172a]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <span className="text-blue-400 font-semibold text-sm uppercase tracking-wider">Process</span>
            <h2 className="text-4xl lg:text-5xl font-display font-bold mt-4 text-white">
              How It <span className="text-transparent bg-clip-text bg-linear-to-r from-blue-400 to-cyan-400">Works</span>
            </h2>
            <p className="text-xl text-gray-400 mt-4">
              Get started in minutes with our simple four-step process
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-4 gap-8">
            {howItWorks.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="relative"
              >
                {/* Connector Line */}
                {index < howItWorks.length - 1 && (
                  <div className="hidden lg:block absolute top-16 left-1/2 w-full h-0.5 bg-gradient-to-r from-blue-500/30 to-cyan-500/30" />
                )}
                
                <div className="bg-gray-800/50 backdrop-blur-sm p-8 rounded-2xl text-center relative border border-gray-700">
                  <div className="w-16 h-16 mx-auto mb-6 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-2xl flex items-center justify-center text-3xl shadow-lg">
                    {item.icon}
                  </div>
                  <div className="absolute -top-3 -right-3 w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold">
                    {item.step}
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-3">{item.title}</h3>
                  <p className="text-gray-400">{item.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-[#0B1120]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <span className="text-blue-400 font-semibold text-sm uppercase tracking-wider">Testimonials</span>
            <h2 className="text-4xl lg:text-5xl font-display font-bold mt-4 text-white">
              What Our <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">Clients Say</span>
            </h2>
            <p className="text-xl text-gray-400 mt-4">
              Join thousands of satisfied customers who have grown their presence with us
            </p>
          </motion.div>

          <Swiper
            modules={[Autoplay, Pagination, EffectCoverflow]}
            effect="coverflow"
            coverflowEffect={{
              rotate: 50,
              stretch: 0,
              depth: 100,
              modifier: 1,
              slideShadows: true,
            }}
            spaceBetween={30}
            slidesPerView={1}
            breakpoints={{
              640: { slidesPerView: 1 },
              768: { slidesPerView: 2 },
              1024: { slidesPerView: 3 },
            }}
            autoplay={{ delay: 5000, disableOnInteraction: false }}
            pagination={{ clickable: true }}
            className="pb-12"
          >
            {testimonials.map((testimonial, index) => (
              <SwiperSlide key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                  viewport={{ once: true }}
                  className="bg-gray-800/50 backdrop-blur-sm p-8 rounded-2xl border border-gray-700 h-full"
                >
                  <div className="flex items-center mb-4">
                    <img
                      src={testimonial.image}
                      alt={testimonial.name}
                      className="w-16 h-16 rounded-full object-cover mr-4 border-2 border-blue-500/30"
                    />
                    <div>
                      <h4 className="font-semibold text-white">{testimonial.name}</h4>
                      <p className="text-sm text-gray-400">{testimonial.role}</p>
                      <p className="text-xs text-blue-400 mt-1">{testimonial.company}</p>
                    </div>
                  </div>
                  <div className="flex mb-3">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <FiStar key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <p className="text-gray-300 italic">"{testimonial.content}"</p>
                </motion.div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </section>

      {/* Trust Badges Section */}
      <section className="py-8 bg-[#0f172a]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center items-center gap-8 opacity-60">
            {['Paystack', 'Korapay', 'Visa', 'Mastercard', 'Transactpay'].map((badge, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-xl font-semibold text-gray-500"
              >
                {badge}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 pb-10 bg-[#0B1120]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="bg-gradient-to-r from-blue-600/20 to-cyan-600/20 backdrop-blur-sm p-12 lg:p-16 rounded-3xl text-center relative overflow-hidden border border-blue-500/20"
          >
            {/* Background Gradient */}
            <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-cyan-500/10" />
            
            {/* Floating Elements */}
            <motion.div
              animate={{ 
                y: [0, -30, 0],
                rotate: [0, 10, 0]
              }}
              transition={{ duration: 10, repeat: Infinity }}
              className="absolute top-10 left-10 w-32 h-32 bg-blue-500/20 rounded-full blur-3xl"
            />
            <motion.div
              animate={{ 
                y: [0, 30, 0],
                rotate: [0, -10, 0]
              }}
              transition={{ duration: 12, repeat: Infinity }}
              className="absolute bottom-10 right-10 w-40 h-40 bg-cyan-500/20 rounded-full blur-3xl"
            />
            
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold mb-6 relative z-10 text-white">
              Ready to <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">Dominate</span> Social Media?
            </h2>
            <p className="text-lg sm:text-xl text-gray-300 mb-10 max-w-2xl mx-auto relative z-10">
              Join thousands of successful businesses and influencers who trust us for their social media growth
            </p>
            <div className="flex flex-wrap gap-4 justify-center relative z-10">
              <Link to="/accounts">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white font-semibold py-3 px-8 rounded-xl hover:shadow-lg hover:shadow-blue-500/25 transition-all duration-300 group"
                >
                  <span className="flex items-center">
                    Start Browsing Now
                    <FiArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
                  </span>
                </motion.button>
              </Link>
              <Link to="/register">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-white/5 backdrop-blur-sm border border-blue-500/20 text-white font-semibold py-3 px-8 rounded-xl hover:bg-white/10 transition-all duration-300"
                >
                  Create Free Account
                </motion.button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
      <SupportButton />
    </>
  );
};